import { useEffect } from "react";
import { useForm } from "react-hook-form";
import useWebsites from "../../data/useWebsites";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IWebsite } from "../../models/Website";
import Modal from "../../layouts/Modal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { TextInput } from "../shared/Inputs/TextInput";

interface WebsiteDisplaySettingsModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  website?: IWebsite;
}

type FormValues = {
  iframeHeight: string;
  zIndex: string;
  showTitle: boolean;
  wrapPageInContent: boolean;
  actionButtonPosition: string;
  hidePreferredCollection: boolean;
  hideSteps: boolean;
  preferredCollectionMinDays: string;
  preferredCollectionMaxDays: string;
  allowWeekends: boolean;
};

export default function WebsiteDisplaySettingsModal({ open, setOpen, website }: WebsiteDisplaySettingsModalProps) {
  const { saveHandlers } = useDefaultCRUDHandlers("Website Display Settings");
  const { getDisplaySetting, upsertDisplaySetting } = useWebsites();
  const { register, handleSubmit, reset } = useForm<FormValues>();

  useEffect(() => {
    if (!open || !website?.id) return;

    getDisplaySetting(website.id)
      .then((data) => {
        reset({
          iframeHeight: data?.iframeHeight?.toString() || "",
          zIndex: data?.zIndex?.toString() || "",
          showTitle: data?.showTitle ?? true,
          wrapPageInContent: data?.wrapPageInContent ?? true,
          actionButtonPosition: data?.actionButtonPosition ?? "page",
          hidePreferredCollection: data?.hidePreferredCollection ?? true,
          hideSteps: data?.hideSteps ?? false,
          preferredCollectionMinDays: data?.preferredCollectionMinDays?.toString() ?? "2",
          preferredCollectionMaxDays: data?.preferredCollectionMaxDays?.toString() ?? "10",
          allowWeekends: data?.allowWeekends ?? false,
        });
      })
      .catch(() => {
        reset({
          iframeHeight: "",
          zIndex: "",
          showTitle: true,
          wrapPageInContent: true,
          actionButtonPosition: "page",
          hidePreferredCollection: true,
          hideSteps: false,
          preferredCollectionMinDays: "2",
          preferredCollectionMaxDays: "10",
          allowWeekends: false,
        });
      });
  }, [open, website?.id]);

  const onSubmit = (data: FormValues) => {
    if (!website?.id) return;

    const parsedHeight = data.iframeHeight?.trim() ? Number(data.iframeHeight.trim()) : null;
    const iframeHeight = parsedHeight !== null && Number.isFinite(parsedHeight) && parsedHeight > 0 ? parsedHeight : null;
    const parsedZIndex = data.zIndex?.trim() ? Number(data.zIndex.trim()) : null;
    const zIndex = parsedZIndex !== null && Number.isInteger(parsedZIndex) ? parsedZIndex : null;

    const parsedMinDays = data.preferredCollectionMinDays?.trim() ? Number(data.preferredCollectionMinDays.trim()) : null;
    const preferredCollectionMinDays = parsedMinDays !== null && Number.isInteger(parsedMinDays) && parsedMinDays >= 0 ? parsedMinDays : null;
    const parsedMaxDays = data.preferredCollectionMaxDays?.trim() ? Number(data.preferredCollectionMaxDays.trim()) : null;
    const preferredCollectionMaxDays = parsedMaxDays !== null && Number.isInteger(parsedMaxDays) && parsedMaxDays >= 0 ? parsedMaxDays : null;

    upsertDisplaySetting.mutate(
      {
        websiteId: website.id,
        iframeHeight,
        zIndex,
        showTitle: data.showTitle ?? true,
        wrapPageInContent: data.wrapPageInContent ?? true,
        actionButtonPosition: data.actionButtonPosition || "page",
        hidePreferredCollection: data.hidePreferredCollection ?? true,
        hideSteps: data.hideSteps ?? false,
        preferredCollectionMinDays,
        preferredCollectionMaxDays,
        allowWeekends: data.allowWeekends ?? false,
      },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
        },
        onError: saveHandlers.onError,
      }
    );
  };

  const toggles: { name: keyof FormValues; label: string; description: string }[] = [
    { name: "showTitle", label: "Show page title section", description: "Display the page heading above the widget." },
    { name: "wrapPageInContent", label: "Wrap page in content border", description: "Surround the page with the content container border." },
    { name: "hidePreferredCollection", label: "Hide preferred collection", description: "Hide the preferred collection selector from users." },
    { name: "hideSteps", label: "Hide steps", description: "Hide the step indicator within the widget." },
    { name: "allowWeekends", label: "Allow weekend collection dates", description: "Allow customers to select Saturday or Sunday as a preferred collection date." },
  ];

  return (
    <Modal width="max-w-md" open={open} setOpen={setOpen}>
      <h1 className="text-xl">Edit Website Display Settings</h1>
      <small className="text-gray-500"><i>Configure widget display values used in embed code</i></small>

      <form className="mt-6 space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Dimensions</h2>
          <div className="grid grid-cols-2 gap-4">
            <TextInput
              label="Iframe Height (px)"
              placeholder="900"
              register={register}
              registerName="iframeHeight"
              type="number"
            />
            <TextInput
              label="Z-Index"
              placeholder="9999"
              register={register}
              registerName="zIndex"
              type="number"
            />
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Preferred Collection Date Range</h2>
          <p className="text-xs text-gray-400">Minimum and maximum days from today the customer can select as their preferred collection date.</p>
          <div className="grid grid-cols-2 gap-4">
            <TextInput
              label="Min Days (default 2)"
              placeholder="2"
              register={register}
              registerName="preferredCollectionMinDays"
              type="number"
            />
            <TextInput
              label="Max Days (default 10)"
              placeholder="10"
              register={register}
              registerName="preferredCollectionMaxDays"
              type="number"
            />
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Layout</h2>
          <div>
            <label className="text-sm font-medium text-gray-500">Action Button Position</label>
            <select
              {...register("actionButtonPosition")}
              className="w-full mt-1 text-sm text-gray-900 border-1 border-gray-300 rounded-md shadow-sm p-2"
            >
              <option value="page">Page</option>
              <option value="content">Content</option>
            </select>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Visibility</h2>
          <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
            {toggles.map((toggle) => (
              <label
                key={toggle.name}
                className="flex cursor-pointer items-start justify-between gap-3 px-3 py-2.5 hover:bg-gray-50"
              >
                <span>
                  <span className="block text-sm font-medium text-gray-700">{toggle.label}</span>
                  <span className="block text-xs text-gray-400">{toggle.description}</span>
                </span>
                <input
                  {...register(toggle.name)}
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
              </label>
            ))}
          </div>
        </section>

        <ButtonInput
          isSubmit={true}
          label={upsertDisplaySetting.isLoading ? "Saving..." : "Save Display Settings"}
          classes="w-full"
        />
      </form>
    </Modal>
  );
}
